import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import ArticleCard from "@/components/base/ArticleCard";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { works } from "@/mocks/works";
import { articles } from "@/mocks/articles";
import { scrollToTop } from "@/hooks/useScrollToTop";
import { SITE } from "@/config/site";

export default function Search() {
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();

  const matchedWorks = q
    ? works.filter(
        (w) =>
          w.title.includes(q) ||
          w.categoryName.includes(q) ||
          w.desc.includes(q) ||
          w.tag.includes(q)
      )
    : [];

  const matchedArticles = q
    ? articles.filter(
        (a) =>
          a.title.includes(q) ||
          a.categoryName.includes(q) ||
          a.summary.includes(q) ||
          a.tags.some((t) => t.includes(q))
      )
    : [];

  const hasResults = matchedWorks.length > 0 || matchedArticles.length > 0;

  useSeo({
    title: q ? `"${q}" 搜索结果 - 抖漫` : "搜索 - 抖漫",
    description: `抖漫搜索"${q}"的结果，包含动态漫画作品与资讯文章。`,
    path: `/search?q=${encodeURIComponent(q)}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "SearchResultsPage",
        name: `"${q}" 搜索结果`,
        url: `${SITE.baseUrl}/search?q=${encodeURIComponent(q)}`,
        inLanguage: "zh-CN",
      },
    ],
  });

  return (
    <Layout>
      <section className="pt-28 pb-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <Reveal>
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: q ? `"${q}" 搜索结果` : "搜索" }]} />
          </Reveal>

          <Reveal delay={80}>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-50 mt-4 mb-2">
              {q ? `「${q}」的搜索结果` : "搜索"}
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              {q
                ? `共找到 ${matchedWorks.length} 部作品、${matchedArticles.length} 篇文章`
                : "请输入关键词搜索作品与文章"}
            </p>
          </Reveal>

          {q && !hasResults && (
            <Reveal delay={120}>
              <div className="py-16 text-center text-foreground-500">
                <i className="ri-search-line text-5xl mb-3 block text-foreground-700"></i>
                <p className="text-lg mb-2">没有找到与「{q}」相关的结果</p>
                <p className="text-sm">尝试搜索其他关键词，或浏览分类页查找作品</p>
                <Link
                  to="/category/xuanhuan"
                  onClick={scrollToTop}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-950 font-medium whitespace-nowrap hover:bg-primary-400 transition-colors"
                >
                  浏览全部作品
                </Link>
              </div>
            </Reveal>
          )}

          {matchedWorks.length > 0 && (
            <Reveal delay={120}>
              <div className="mb-10">
                <h2 className="font-heading text-xl text-foreground-50 mb-4">作品</h2>
                <div className="grid grid-cols-2 gap-4 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
                  {matchedWorks.map((w) => (
                    <WorkCard key={w.id} work={w} />
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {matchedArticles.length > 0 && (
            <Reveal delay={160}>
              <div className="mb-10">
                <h2 className="font-heading text-xl text-foreground-50 mb-4">文章资讯</h2>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {matchedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </Layout>
  );
}